import { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
} from "./access/firestore";

import { db } from "./firebase";
import { useClinic } from "./context/ClinicContext";
import {
  CalendarClock,
  ChevronLeft,
  ClipboardList,
  Clock3,
  ShieldCheck,
  UserRound,
} from "lucide-react";

const convertirFecha = (valor) => {
  if (valor == null || valor === "") return null;

  try {
    const fecha = typeof valor.toDate === "function"
      ? valor.toDate()
      : typeof valor.seconds === "number"
        ? new Date(valor.seconds * 1000)
        : new Date(valor);
    return fecha instanceof Date && !Number.isNaN(fecha.getTime()) ? fecha : null;
  } catch {
    return null;
  }
};

const formatoFecha = (valor) => {
  const fecha = convertirFecha(valor);

  if (!fecha) return "No disponible";

  return fecha.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatoHora = (valor) => {
  const fecha = convertirFecha(valor);

  if (!fecha) return "No disponible";

  return fecha.toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const obtenerUsuario = (log) => {
  const actor = log.actor || {};
  const datos = log.after || log.before || {};

  return (
    log.userName ||
    log.usuarioNombre ||
    log.nombreUsuario ||
    actor.userName ||
    datos.updatedByName ||
    datos.createdByName ||
    datos.nombreUsuario ||
    datos.userName ||
    actor.userEmail ||
    log.userEmail ||
    "VexaDent / Sistema"
  );
};

const obtenerModulo = (log) => {
  if (log.module) return log.module;
  if (log.modulo) return log.modulo;

  const ruta = log.documentPath || "";
  const raiz = ruta.split("/")[0];

  if (ruta.includes("cuentasPorCobrar")) {
    return "Cuentas por cobrar";
  }

  if (ruta.includes("ortodoncia")) {
    return "Ortodoncia";
  }

  const modulos = {
    pacientes: "Pacientes",
    citas: "Citas",
    pagos: "Finanzas",
    users: "Usuarios",
    whatsappConversations: "WhatsApp",
    prescriptions: "Recetas",
    settings: "Configuración",
    integrations: "Integraciones",
  };

  return modulos[raiz] || raiz || "General";
};

const obtenerNombre = (datos) => {
  if (!datos) return "";

  return (
    datos.nombreCompleto ||
    [datos.nombre, datos.apellidos].filter(Boolean).join(" ") ||
    datos.pacienteNombre ||
    datos.nombrePaciente ||
    datos.nombreUsuario ||
    datos.userName ||
    datos.nombreClinica ||
    datos.concepto ||
    datos.motivo ||
    datos.titulo ||
    ""
  );
};

const obtenerFechaCita = (datos) => {
  const valor =
    datos.fecha ||
    datos.fechaCita ||
    datos.start ||
    datos.startDate ||
    datos.fechaHora;

  const fecha = convertirFecha(valor);

  if (!fecha) return "";

  const dia = fecha.toLocaleDateString("es-MX", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const hora = fecha.toLocaleTimeString("es-MX", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${dia} a las ${hora}`;
};

const obtenerAccion = (log) => {
  if (log.description) return log.description;
  if (log.descripcion) return log.descripcion;
  if (log.detalle) return log.detalle;

  const modulo = obtenerModulo(log);
  const accion = String(log.action || log.accion || "").toUpperCase();
  const datos = log.after || log.before || log;
  const objetivo =
    log.targetName ||
    obtenerNombre(datos) ||
    log.documentId ||
    "registro";

  if (modulo === "Finanzas") {
    const monto =
      datos.monto ??
      datos.cantidad ??
      datos.total ??
      datos.importe;

    if (accion === "CREAR" || accion === "CREATE" || accion === "PAGO_REGISTRADO") {
      if (monto !== undefined && monto !== null) {
        return `Pago registrado por ${Number(monto).toLocaleString(
          "es-MX",
          { style: "currency", currency: "MXN" }
        )}, paciente ${datos.pacienteNombre || datos.nombrePaciente || objetivo}`;
      }

      return `Pago registrado, paciente ${objetivo}`;
    }

    if (accion === "ELIMINAR" || accion === "DELETE") {
      return `Pago eliminado, paciente ${objetivo}`;
    }

    return `Pago modificado, paciente ${objetivo}`;
  }

  if (modulo === "Citas") {
    const paciente =
      datos.pacienteNombre ||
      datos.nombrePaciente ||
      datos.nombre ||
      objetivo;

    const fechaCita = obtenerFechaCita(datos);

    if (accion === "CREAR" || accion === "CREATE") {
      return `Cita creada${fechaCita ? ` para el ${fechaCita}` : ""}, paciente ${paciente}`;
    }

    if (accion === "ELIMINAR" || accion === "DELETE") {
      return `Cita eliminada${fechaCita ? ` programada para el ${fechaCita}` : ""}, paciente ${paciente}`;
    }

    return `Cita modificada${fechaCita ? ` para el ${fechaCita}` : ""}, paciente ${paciente}`;
  }

  if (modulo === "Usuarios") {
    const nombreUsuario =
      obtenerNombre(datos) ||
      log.targetName ||
      "usuario";

    const rol = datos.rol || datos.role || "";

    if (accion === "CREAR" || accion === "CREATE") {
      return `Usuario ${nombreUsuario} agregado${rol ? `, rol: ${rol}` : ""}`;
    }

    if (accion === "ELIMINAR" || accion === "DELETE") {
      return `Usuario ${nombreUsuario} eliminado`;
    }

    return `Usuario ${nombreUsuario} modificado${rol ? `, rol: ${rol}` : ""}`;
  }

  if (modulo === "Pacientes") {
    if (accion === "CREAR" || accion === "CREATE") {
      return `Paciente ${objetivo} registrado`;
    }

    if (accion === "ELIMINAR" || accion === "DELETE") {
      return `Paciente ${objetivo} eliminado`;
    }

    return `Expediente de ${objetivo} actualizado`;
  }

  const acciones = {
    CREAR: "Registro creado",
    CREATE: "Registro creado",
    MODIFICAR: "Registro modificado",
    UPDATE: "Registro modificado",
    ELIMINAR: "Registro eliminado",
    DELETE: "Registro eliminado",
  };

  return `${acciones[accion] || "Actividad registrada"}: ${modulo}${objetivo ? `, ${objetivo}` : ""}`;
};

const normalizarLog = (documento, origen) => {
  const datos = documento.data() || {};
  const fecha = convertirFecha(
    datos.createdAt || datos.timestamp || datos.fecha
  );

  return {
    ...datos,
    id: `${origen}_${documento.id}`,
    origen,
    fechaOrden: fecha ? fecha.getTime() : 0,
  };
};

const etiquetaOrigen = (origen) =>
  origen === "automatico" ? "Auditoría automática" : "Actividad registrada";

export default function ActivityLogs({ mobile = false, onBack } = {}) {
  const { clinicId } = useClinic();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLogs([]);
    setError("");
    if (!clinicId) {
      setLogs([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    let active = true;

    let manuales = [];
    let automaticos = [];
    let manualListo = false;
    let automaticoListo = false;

    const actualizar = () => {
      if (!active) return;
      setLogs(
        [...manuales, ...automaticos]
          .sort((a, b) => b.fechaOrden - a.fechaOrden)
          .slice(0, 500)
      );

      if (manualListo && automaticoListo) {
        setLoading(false);
      }
    };

    const manejarError = (err) => {
      if (!active) return;
      console.error("Error cargando auditoría:", err);
      setError(
        "No se pudieron cargar los registros. Revisa los permisos de Firestore."
      );
      setLoading(false);
    };

    const cancelarManuales = onSnapshot(
      query(
        collection(db, "clinics", clinicId, "logs"),
        orderBy("createdAt", "desc"),
        limit(500)
      ),
      (snapshot) => {
        if (!active) return;
        manuales = snapshot.docs.map((doc) =>
          normalizarLog(doc, "manual")
        );

        manualListo = true;
        actualizar();
      },
      manejarError
    );

    const cancelarAutomaticos = onSnapshot(
      query(
        collection(db, "clinics", clinicId, "auditLogs"),
        orderBy("createdAt", "desc"),
        limit(500)
      ),
      (snapshot) => {
        if (!active) return;
        automaticos = snapshot.docs.map((doc) =>
          normalizarLog(doc, "automatico")
        );

        automaticoListo = true;
        actualizar();
      },
      manejarError
    );

    return () => {
      active = false;
      cancelarManuales();
      cancelarAutomaticos();
    };
  }, [clinicId]);

  if (loading) {
    return (
      <div className="p-6 text-center" role="status">
        Cargando historial de actividades...
      </div>
    );
  }

  return (
    <div className="vd-module-page vd-activity-page space-y-4 p-4">
      {mobile && onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Volver a Más"
          style={{ display: "flex", alignItems: "center", gap: 6, minHeight: 44, color: "#2563eb", background: "none", border: 0, cursor: "pointer" }}
        >
          <ChevronLeft size={20} />
          Volver a Más
        </button>
      )}
      <header className="vd-page-heading">
        <div className="vd-page-heading-icon"><ClipboardList size={23} /></div>
        <div>
          <p className="vd-page-heading-eyebrow">Transparencia</p>
          <h1>Historial de actividades</h1>
          <p className="vd-page-heading-subtitle">Registro de las operaciones realizadas en la clínica.</p>
        </div>
      </header>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
          {error}
        </div>
      )}

      {!logs.length && !error && (
        <div className="rounded-lg border p-6 text-center text-gray-500">
          No hay actividades registradas.
        </div>
      )}

      <div className="vd-activity-list" role="list">
        {logs.map((log) => {
          const fechaRegistro =
            log.createdAt || log.timestamp || log.fecha;
          const usuario = obtenerUsuario(log);
          const accion = obtenerAccion(log);
          const modulo = obtenerModulo(log);

          return (
            <article
              key={log.id}
              className="vd-activity-card"
            >
              <div className="vd-activity-card-heading">
                <div className="vd-activity-card-mark" aria-hidden="true">
                  <ShieldCheck size={18} />
                </div>
                <div className="vd-activity-card-title">
                  <div className="vd-activity-card-kicker">
                    <span className="vd-activity-module">{modulo}</span>
                    <span className="vd-activity-origin">{etiquetaOrigen(log.origen)}</span>
                  </div>
                  <strong>{accion}</strong>
                </div>
                <div className="vd-activity-card-time">
                  <Clock3 size={15} />
                  <span><em>Fecha:</em> {formatoFecha(fechaRegistro)}</span>
                  <b><em>Hora:</em> {formatoHora(fechaRegistro)}</b>
                </div>
              </div>
              <div className="vd-activity-card-details">
                <div className="vd-activity-detail">
                  <UserRound size={16} />
                  <span>
                    <small>Usuario</small>
                    <strong>{usuario}</strong>
                  </span>
                </div>
                <div className="vd-activity-detail">
                  <CalendarClock size={16} />
                  <span>
                    <small>Fecha y hora</small>
                    <strong>{formatoFecha(fechaRegistro)} · {formatoHora(fechaRegistro)}</strong>
                  </span>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
